// Intelligence concourse map.
//
// Two rows of phase-aware GateCards, positioned along the concourse
// using the real GATE_POSITIONS percentages. North gates (odd) on top,
// south gates (even) below — paired vertically by their actual
// position (e.g. C66 sits directly under C67, C24 under C23). West
// and East wings are separated by a small gap matching the real
// terminal break.
//
// No SVG world canvas, no zoom transform — cards render at native
// readable size; the container scrolls horizontally if the concourse
// is wider than the viewport.

import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useIntel } from './IntelligenceContext';
import { GATE_DETAILS, nextInboundFor } from './mockGateDetails';
import {
  GATE_POSITIONS, WEST_PROPORTION, EAST_PROPORTION,
} from '../../flight-data/gatePositions';
import { C } from '../styles';
import Workspace from './Workspace';
import GateCard, { CARD_W, CARD_H, LABEL_H } from './GateCard';

// World canvas dimensions (px). Wide enough that the GATE_POSITIONS
// percentages (which encode the real concourse spacing) translate to
// gaps big enough that cards don't overlap. The tighter clusters
// (e.g. WEST 23-31 at 10% intervals) still read as tight; the
// tunnel gaps (jumps to 57% / 90%) read as wide visual breaks.
const WORLD_W = 7500;
const WORLD_H = 500;

// Horizontal layout
const MARGIN = 140;                  // > half-card-width + small padding
const WING_GAP = 220;                // visual break between West and East wings
const USEABLE_W = WORLD_W - 2 * MARGIN - WING_GAP;
const WEST_REGION_W = USEABLE_W * (WEST_PROPORTION / 100);
const EAST_REGION_W = USEABLE_W * (EAST_PROPORTION / 100);

// Vertical layout
const NORTH_TOP = 30;
const ROW_GAP   = 70;
const SOUTH_TOP = NORTH_TOP + LABEL_H + CARD_H + ROW_GAP;

// Convert a gate number into a world-pixel center x.
function gateCenterX(num) {
  const pos = GATE_POSITIONS[num];
  if (!pos) return 0;
  const pct = parseFloat(pos.left);
  if (pos.wing === 'west') {
    return MARGIN + WEST_REGION_W * (pct / 100);
  }
  return MARGIN + WEST_REGION_W + WING_GAP + EAST_REGION_W * (pct / 100);
}

const ALL_GATE_NUMS = Object.keys(GATE_POSITIONS).map(Number);

function ConcourseMap() {
  const intel = useIntel();
  const { selected } = intel;

  const scrollRef = useRef(null);

  // Auto-scroll the selected gate (or the gate hosting the selected
  // flight) into the center of the viewport. World is much wider than
  // the screen so without this, search results / alert clicks for an
  // off-screen gate would feel broken.
  useEffect(() => {
    if (!selected) return;
    let gateNum = null;
    if (selected.kind === 'gate') {
      gateNum = parseInt(selected.id.replace(/^C/, ''), 10);
    } else if (selected.kind === 'flight') {
      for (const d of GATE_DETAILS.values()) {
        if (d.flightNumber === selected.id) {
          gateNum = d.gateNum;
          break;
        }
      }
    }
    if (gateNum == null) return;
    const el = scrollRef.current;
    if (!el) return;
    const cx = gateCenterX(gateNum);
    const target = cx - el.clientWidth / 2;
    el.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [selected]);

  return (
    <Box sx={{
      flex: 1, minHeight: 0, position: 'relative',
      backgroundColor: '#0a0d14',
      overflow: 'hidden',
    }}>
      {/* Apron grid background — purely decorative */}
      <Box sx={{
        position: 'absolute', inset: 0,
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
        pointerEvents: 'none',
      }} />

      {/* Scroll container */}
      <Box ref={scrollRef} sx={{
        position: 'absolute', inset: 0,
        overflow: 'auto',
        '&::-webkit-scrollbar': { height: 10, width: 10 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(255, 255, 255, 0.18)', borderRadius: 5,
        },
      }}>
        {/* Fixed-size world that holds the absolutely-positioned cards */}
        <Box sx={{
          position: 'relative',
          width: WORLD_W, height: WORLD_H,
        }}>
          {ALL_GATE_NUMS.map(num => {
            const id = `C${num}`;
            const pos = GATE_POSITIONS[num];
            const detail = GATE_DETAILS.get(id) || emptyDetail(num, id);
            const x = gateCenterX(num);
            const top = pos.side === 'north' ? NORTH_TOP : SOUTH_TOP;
            return (
              <Box
                key={num}
                sx={{
                  position: 'absolute',
                  left: x - CARD_W / 2,
                  top,
                  width: CARD_W,
                }}
              >
                <GateCard entity={{ kind: 'gate', id }} detail={detail} />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Overlays */}
      <Workspace />

      {/* Phase legend (bottom-left) */}
      <Box
        data-intel-overlay
        sx={{
          position: 'absolute', bottom: 12, left: 12,
          backgroundColor: 'rgba(15, 18, 28, 0.85)',
          border: `1px solid ${C.border}`,
          borderRadius: 1, px: 1, py: 0.5,
          display: 'flex', alignItems: 'center', gap: 1.25,
          zIndex: 10,
        }}
      >
        <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Phase
        </Typography>
        {[
          ['#0277BD', 'Inbound'],
          ['#00BCD4', 'Active'],
          ['#F6530F', 'Push'],
          ['#FF0000', 'Issue'],
        ].map(([color, label]) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: 0.4, backgroundColor: color }} />
            <Typography sx={{ fontSize: '0.6rem', color: C.textSecondary }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function emptyDetail(num, id) {
  return {
    gateId: id,
    gateNum: num,
    flightNumber: null,
    origin: null,
    tail: null,
    eta: null,
    etd: null,
    state: 'empty',
    drivers: [],
    bins: [],
    totalBags: 0,
    scannedBags: 0,
    bagPct: 0,
    connections: [],
    tug: null,
    // Pull the real next inbound from the BANK so the empty gate shows
    // when the next aircraft arrives, not a hardcoded "Nothing scheduled".
    nextInbound: nextInboundFor(id),
    phase: 'empty',
    phaseStartedSecAgo: 0,
    issues: [],
  };
}

export default ConcourseMap;
